import React, { memo, useState, useCallback } from 'react';
import history from 'historyContainer';
import { TableOverflowWrapper, TableOverflowScroll } from 'theme/Global.styles';
import { Link } from 'react-router-dom';
import { Button } from 'theme/Button';
import { Tag } from '@zendeskgarden/react-tags';
import numeral from 'numeral';
import formatSortFields from 'utility/formatSortFields';
import { Col, Row } from '@zendeskgarden/react-grid';
import NoDataPlaceholder, {
  FormattedEmpty,
} from 'components/NoDataPlaceholder/NoDataPlaceholder';
import Spinner from 'components/Spinner/Spinner';
import { map, get, omitBy, noop } from 'lodash';
import { SM } from '@zendeskgarden/react-typography';
import Pagination from 'components/Pagination/Pagination';
import { trackClick } from 'utility/analytics';

import { variables } from 'theme/variables';
import { paginationShape } from 'propTypesObjects';
import { PropTypes } from 'prop-types';
import {
  TableHead,
  TableBody,
  StyledTable,
  PublicProductsTableWrapper,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  TableCell,
  TableSortableCell,
} from './PublicProductsTable.styles';

const { custom_navy: customNavy } = variables;

const colorMap = {
  'CH Nickel': { red: true },
  'Duplex Stainless': { blue: true },
  'Martensitic Stainless': { orange: true },
  'PH Nickel': null,
};

function determineWidths(fieldSettings = {}) {
  const buttonDisabled = get(fieldSettings, 'button.disabled');
  const widths = {
    name: {
      active: true,
      size: '26%',
      title: 'name',
      fontSize: 'SM',
      expandOnHover: true,
      renderFn: (item) =>
        buttonDisabled ? (
          <SM className="expandable" as="h3" ellipsis style={{ opacity: 1 }}>
            <Link to={`/p/${item.slug}/${item.id}`}>
              <span style={{ color: customNavy }}>{item.name}</span>
            </Link>
          </SM>
        ) : (
          <SM className="expandable" as="h3" ellipsis style={{ opacity: 1 }}>
            {item.name}
          </SM>
        ),
    },
    metallurgy_group: {
      active: true,
      suffix: '',
      prefix: '',
      size: '15%',
      title: 'Alloy Group',
      sortable: false,
      renderFn: (item) => {
        const value = item.metallurgy_group_name;
        return (
          <div style={{ maxWidth: '100%' }}>
            <Tag
              // LINT OVERRIDE #3
              // Component wraps another component
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(colorMap[value] || {})}
            >
              <SM light style={{ fontSize: '11px' }} ellipsis>
                {value}
              </SM>
            </Tag>
          </div>
        );
      },
    },
    end_finish: {
      active: false,
      suffix: '',
      prefix: '',
      size: '12%',
      fontSize: 'SM',
      title: 'End Finish',
      sortable: false,
      accessor: 'end_finish_name',
    },
    od: {
      active: false,
      fontSize: 'SM',
      formatter: (v) => numeral(v).format('0,0.000'),
      suffix: 'in.',
      prefix: '',
      size: '12%',
      title: 'Nominal OD',
      sortable: false,
    },
    inside_diameter: {
      active: true,
      fontSize: 'SM',
      formatter: (v) => numeral(v).format('0,0.000'),
      suffix: 'in.',
      prefix: '',
      size: '10%',
      title: 'Nominal ID',
      sortable: false,
    },
    weight_per_foot: {
      active: false,
      fontSize: 'SM',
      formatter: (v) => numeral(v).format('0,0.000'),
      suffix: 'lb.',
      prefix: '',
      size: '10%',
      title: '#/Ft',
      sortable: false,
    },
    wall: {
      active: true,
      fontSize: 'SM',
      formatter: (v) => numeral(v).format('0,0.000'),
      suffix: 'in.',
      prefix: '',
      size: '10%',
      title: 'Nominal Wall',
      sortable: false,
    },
    drift: {
      active: true,
      fontSize: 'SM',
      formatter: (v) => numeral(v).format('0,0.000'),
      suffix: 'in.',
      prefix: '',
      size: '10%',
      title: 'Drift',
      sortable: false,
    },
    collapse_pressure: {
      active: true,
      grey: false,
      fontSize: 'SM',
      formatter: (v) => numeral(v).format('0,0'),
      suffix: 'PSI',
      prefix: '',
      size: '14%',
      title: 'Collapse',
      sortable: false,
    },
    body_yield_strength: {
      active: false,
      grey: false,
      fontSize: 'SM',
      formatter: (v) => `${numeral(v).format('0,0')}`,
      suffix: 'KSI',
      prefix: '',
      size: '14%',
      title: 'Body Yield',
      sortable: false,
    },
    api_internal_pressure: {
      active: true,
      grey: false,
      fontSize: 'SM',
      formatter: (v) => numeral(v).format('0,0'),
      suffix: 'PSI',
      prefix: '',
      size: '15%',
      title: 'Int. / Burst Pressure',
      sortable: false,
    },
    button: {
      active: !buttonDisabled,
      grey: false,
      size: '8%',
      title: 'button',
      headerUseRender: true,
      cellProps: {
        justifyEnd: true,
        style: { paddingLeft: 0, paddingRight: '14px' },
      },
      renderFn: (item, index, style) => (
        <Button style={style || { fontSize: '11px' }} miniTableIcon primary>
          {' '}
          {!item ? null : (
            <Link style={{ color: '#fff' }} to={`/p/${item.slug}/${item.id}`}>
              View
            </Link>
          )}
        </Button>
      ),
    },
  };
  return widths;
}

const itemsDefault = [];

function PublicProductsTable({
  loading,
  items = itemsDefault,
  fieldSettings,
  pagination,
  style,
  onSort,
  fetchMore,
  tableStyle,
  onEdit = noop,
  onSelect,
}) {
  const [widths] = useState(
    omitBy(determineWidths(fieldSettings), (w) => !w.active)
  );

  const renderTable = useCallback(() => {
    if (loading && !items.length) {
      return (
        <NoDataPlaceholder>
          <Spinner />
        </NoDataPlaceholder>
      );
    }
    if (!items.length && !pagination) {
      return <FormattedEmpty />;
    }
    return (
      <StyledTable border style={style || { borderTop: 'none' }}>
        {loading ? (
          <div
            style={{
              background: 'rgba(255,255,255,0.5)',
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              left: 0,
              display: 'flex',
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            <Spinner size="20px" />
          </div>
        ) : null}
        <TableOverflowScroll>
          <TableOverflowWrapper minWidth="900px">
            <ListingsTableHeader
              widths={widths}
              pagination={pagination}
              onSort={onSort}
              fetchMore={fetchMore}
            />
            <TableBody pagination={pagination} style={tableStyle}>
              {items.length ? (
                <ListingItemBody
                  onEdit={onEdit}
                  onSelect={onSelect}
                  widths={widths}
                  data={items}
                />
              ) : (
                <FormattedEmpty />
              )}
            </TableBody>
          </TableOverflowWrapper>
        </TableOverflowScroll>

        {pagination ? (
          <Pagination public fetchMore={fetchMore} pagination={pagination} />
        ) : null}
      </StyledTable>
    );
  }, [
    fetchMore,
    items,
    loading,
    onEdit,
    onSelect,
    onSort,
    pagination,
    style,
    tableStyle,
    widths,
  ]);

  return (
    <PublicProductsTableWrapper style={{ position: 'relative' }}>
      <Row>
        <Col size="12">{renderTable()}</Col>
      </Row>
    </PublicProductsTableWrapper>
  );
}

PublicProductsTable.propTypes = {
  loading: PropTypes.bool,
  // LINT OVERRIDE #8
  // TECH DEBT
  // Array has undetermined elements
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.array,
  fieldSettings: PropTypes.shape({}),
  pagination: paginationShape,
  style: PropTypes.shape({}),
  onSort: PropTypes.func,
  fetchMore: PropTypes.func,
  tableStyle: PropTypes.string,
  onEdit: PropTypes.func,
  onSelect: PropTypes.func,
};

const ListingItemBody = ({ data, widths, onSelect, onEdit }) =>
  map(data, (item, i) => (
    <CustomerRowItem
      widths={widths}
      key={i}
      index={i}
      item={item}
      onEdit={onEdit}
      onSelect={onSelect}
    />
  ));

function CustomerRowItem({ item, index, widths, onSelect }) {
  const onClickSelect = useCallback(() => {
    trackClick('onSelectCustomer', 'tableRow', 'ListingsTable');
    onSelect(item);
  }, [item, onSelect]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => history.push(`/p/${item.slug}/${item.id}`)}
      onKeyUp={() => history.push(`/p/${item.slug}/${item.id}`)}
    >
      <TableRow
        alignCenter
        onClick={onSelect ? () => onClickSelect(item) : null}
      >
        {map(
          widths,
          (
            {
              expandOnHover,
              fontSize,
              cellProps = {},
              size,
              accessor,
              renderFn,
              ellipsis,
              suffixStyle,
              suffix,
              prefix,
              formatter = (v) => v,
            },
            key
          ) => (
            <TableCell
              expandOnHover={expandOnHover}
              className={`cell_${key}`}
              alignCenter
              key={key}
              width={size}
              fontSize={fontSize}
              // LINT OVERRIDE #3
              // Component wraps another component
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...cellProps}
            >
              {renderFn && renderFn(item, index)}
              {!renderFn && item[accessor || key] ? (
                <SM
                  className={suffix ? `with-suffix` : ''}
                  as="h3"
                  ellipsis={ellipsis}
                >
                  {prefix} {formatter(item[accessor || key])}{' '}
                  <SM
                    style={
                      suffixStyle || { fontSize: '12px', color: '#76899e' }
                    }
                    as="span"
                    className="suffix"
                  >
                    {suffix}
                  </SM>
                </SM>
              ) : (
                <SM
                  className={suffix ? `with-suffix` : ''}
                  as="h3"
                  ellipsis={ellipsis}
                />
              )}
            </TableCell>
          )
        )}
      </TableRow>
    </div>
  );
}

CustomerRowItem.propTypes = {
  item: PropTypes.shape({
    slug: PropTypes.string,
    id: PropTypes.number,
  }),
  index: PropTypes.number,
  widths: PropTypes.shape({}),
  onSelect: PropTypes.func,
};

memo(CustomerRowItem, (pp, np) => pp.item.id === np.item.id);

function ListingsTableHeader({ pagination, widths, fetchMore = noop }) {
  const { active } = pagination;
  const { sort } = active || {};

  const onClickSort = useCallback(
    ({ field }) => {
      const data = formatSortFields({ field, sort });
      fetchMore({
        ...data,
        filters: pagination.active.filters,
        pagination: pagination.active.pagination,
      });
    },
    [fetchMore, pagination.active.filters, pagination.active.pagination, sort]
  );

  return (
    <TableHead>
      <TableHeaderRow>
        {map(
          widths,
          ({ grey, renderFn, headerUseRender, title, size, sortable }, key) => {
            const Component = sortable ? TableSortableCell : TableHeaderCell;
            return (
              <Component
                alignCenter
                grey={grey}
                className={`cell_${key}`}
                sort={(sort.sort === key && sort.direction) || undefined}
                onClick={sortable ? () => onClickSort({ field: key }) : null}
                key={key}
                width={size}
              >
                {headerUseRender ? (
                  renderFn(null, null, { opacity: 0, height: 0 })
                ) : (
                  <SM as="h3" style={{ lineHeight: '14px', display: 'block' }}>
                    {title}
                  </SM>
                )}
              </Component>
            );
          }
        )}
      </TableHeaderRow>
    </TableHead>
  );
}

ListingsTableHeader.propTypes = {
  pagination: paginationShape,
  widths: PropTypes.shape({}),
  fetchMore: PropTypes.func,
};

export default PublicProductsTable;
